// Roles tab — live, org-scoped role catalog (DB ⇄ tenant Keycloak).
// Lists roles from GET /api/roles (Keycloak roles joined with the DB catalog:
// permissions, displayName, syncStatus), lets a roles:manage admin create a
// role (persisted in the DB AND pushed into the business's Keycloak), retry a
// failed Keycloak sync, and delete non-system roles.
import { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Button, useTheme, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControlLabel, Checkbox, Chip, CircularProgress,
  IconButton, Tooltip,
} from "@mui/material";
import { tokens } from "../../../theme";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SyncIcon from "@mui/icons-material/Sync";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { MONO_FONT, getRoleMeta } from "../data/mockUsersRoles";
import { AvatarStack } from "./shared";
import { useAuth } from "../../../contexts/AuthContext";
import DeleteRoleModal from "../modals/DeleteRoleModal";
import {
  listRoles, createRole, deleteRole, syncRole, getMyPermissions, setDefaultRole,
} from "../api";

// Starter capability taxonomy shown in the Create-Role permission editor.
// These are data, not code — the backend `can()` check reads the same keys.
const PERMISSION_OPTIONS = [
  ["users:manage", "Manage members (invite, edit, remove, assign roles)"],
  ["roles:manage", "Create / edit / delete roles"],
  ["members:read", "View the member directory"],
  ["datasets:read", "View datasets"],
  ["datasets:write", "Upload / edit datasets"],
  ["models:read", "View models"],
  ["training:run", "Launch training runs"],
];

const PermItem = ({ label, denied }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, py: 0.3 }}>
      {denied ? (
        <HighlightOffIcon sx={{ fontSize: 13, color: colors.grey[500], opacity: 0.6 }} />
      ) : (
        <CheckCircleOutlineIcon sx={{ fontSize: 13, color: "#16a34a" }} />
      )}
      <Typography sx={{ fontSize: "0.6875rem", color: denied ? colors.grey[500] : colors.grey[400] }}>
        {label}
      </Typography>
    </Box>
  );
};

const SyncBadge = ({ status }) => {
  const map = {
    synced: { label: "Synced to Keycloak", color: "#16a34a", bg: "rgba(22,163,74,0.10)" },
    failed: { label: "Keycloak sync failed", color: "#dc2626", bg: "rgba(220,38,38,0.10)" },
    pending: { label: "Sync pending", color: "#d97706", bg: "rgba(217,119,6,0.10)" },
  };
  const s = map[status] || map.pending;
  return (
    <Chip
      size="small"
      label={s.label}
      sx={{ height: 18, fontSize: "0.5625rem", fontWeight: 600, color: s.color,
            backgroundColor: s.bg, fontFamily: MONO_FONT }}
    />
  );
};

const RolesTab = ({ users = [], showToast }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user, hasRole } = useAuth();

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [perms, setPerms] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", displayName: "", description: "", permissions: {} });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const canManage = useMemo(
    () => Boolean(perms["roles:manage"]) || hasRole?.("platform_superadmin") || hasRole?.("org_admin"),
    [perms, hasRole]
  );

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listRoles();
      setRoles(Array.isArray(data) ? data : []);
    } catch (e) {
      setError("Could not load roles from the BFF.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    getMyPermissions(user?.roles || []).then(setPerms).catch(() => setPerms({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async () => {
    const name = (form.name || "").trim().toLowerCase();
    if (!/^[a-z][a-z0-9_]{1,62}$/.test(name)) {
      showToast?.("Role name must be a lowercase slug (a–z, 0–9, _).", "error");
      return;
    }
    setBusy(true);
    try {
      const res = await createRole({
        name,
        displayName: form.displayName || name,
        description: form.description || "",
        permissions: form.permissions || {},
      });
      if (res?.syncStatus === "failed") {
        showToast?.("Role saved to the catalog, but Keycloak sync failed. Use Retry.", "warning");
      } else {
        showToast?.(`Role “${name}” created and synced to Keycloak.`, "success");
      }
      setDialogOpen(false);
      setForm({ name: "", displayName: "", description: "", permissions: {} });
      load();
    } catch (e) {
      showToast?.(e?.message || "Could not create role.", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleSync = async (name) => {
    try {
      await syncRole(name);
      showToast?.(`Re-synced “${name}” to Keycloak.`, "success");
      load();
    } catch (e) {
      showToast?.(e?.message || "Sync failed.", "error");
    }
  };

  // Confirm from the modal: delete, then surface how many members were moved to
  // the default role vs. kept another role.
  const confirmDelete = async (role) => {
    try {
      const res = await deleteRole(role.name);
      const moved = res?.reassigned ?? 0;
      const kept = res?.keptOther ?? 0;
      const parts = [];
      if (moved) parts.push(`${moved} moved to “${res?.defaultRole || "member"}”`);
      if (kept) parts.push(`${kept} kept another role`);
      showToast?.(
        `Deleted role “${role.name}”.${parts.length ? " " + parts.join(", ") + "." : ""}`,
        "success",
      );
      setDeleteTarget(null);
      load();
    } catch (e) {
      showToast?.(e?.message || "Could not delete role.", "error");
      setDeleteTarget(null);
    }
  };

  const handleSetDefault = async (name) => {
    try {
      await setDefaultRole(name);
      showToast?.(`“${name}” is now the default fallback role.`, "success");
      load();
    } catch (e) {
      showToast?.(e?.message || "Could not set default role.", "error");
    }
  };

  const togglePerm = (key) =>
    setForm((f) => ({ ...f, permissions: { ...f.permissions, [key]: !f.permissions[key] } }));

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 1.5, flexWrap: "wrap" }}>
        <Typography sx={{ fontSize: "0.75rem", color: colors.grey[500], maxWidth: 720 }}>
          Roles are catalogued here and mapped into this business’s own Keycloak realm. Creating
          a role writes it to the database and pushes it into Keycloak, so the{" "}
          <Box component="span" sx={{ fontFamily: MONO_FONT, color: colors.secondary[500] }}>realm_roles</Box>{" "}
          claim stays the source of truth on every request.
        </Typography>
        <Tooltip title={canManage ? "" : "Requires the roles:manage permission"}>
          <span>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!canManage}
              onClick={() => setDialogOpen(true)}
            >
              Create Role
            </Button>
          </span>
        </Tooltip>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 4, justifyContent: "center" }}>
          <CircularProgress size={18} />
          <Typography sx={{ fontSize: "0.75rem", color: colors.grey[500] }}>Loading roles…</Typography>
        </Box>
      )}

      {error && !loading && (
        <Box sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}`,
                   color: "var(--fx-warning-text)", fontSize: "0.75rem", mb: 2 }}>
          {error} <Button size="small" onClick={load}>Retry</Button>
        </Box>
      )}

      {!loading && !error && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: 1.75 }}>
          {roles.map((role) => {
            const meta = getRoleMeta(role.name);
            const members = users.filter((u) => (u.roles || []).includes(role.name));
            const permsEntries = Object.entries(role.permissions || {});
            const allowed = permsEntries.filter(([, v]) => v === true).map(([k]) => k);
            const denied = permsEntries.filter(([, v]) => v === false).map(([k]) => k);
            return (
              <Box
                key={role.name}
                sx={{ backgroundColor: "var(--fx-paper)", border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2, overflow: "hidden", transition: "border-color 0.15s ease",
                      "&:hover": { borderColor: colors.primary[300] } }}
              >
                {/* Header */}
                <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}`,
                           display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: meta.color, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "var(--fx-text-primary)" }}>
                        {role.displayName || meta.label || role.name}
                      </Typography>
                      {role.isSystem && (
                        <Chip size="small" label="built-in" sx={{ height: 16, fontSize: "0.5rem", fontFamily: MONO_FONT }} />
                      )}
                      {role.isDefault && (
                        <Chip
                          size="small"
                          icon={<StarIcon sx={{ fontSize: "0.7rem !important" }} />}
                          label="default"
                          sx={{ height: 16, fontSize: "0.5rem", fontFamily: MONO_FONT,
                                color: "#b45309", backgroundColor: "rgba(217,119,6,0.12)" }}
                        />
                      )}
                    </Box>
                    <Typography sx={{ fontFamily: MONO_FONT, fontSize: "0.625rem", color: colors.grey[500], mt: 0.3 }}>
                      Keycloak role: {role.name}
                    </Typography>
                    <Box sx={{ mt: 0.6 }}>
                      <SyncBadge status={role.syncStatus} />
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
                    <Typography sx={{ fontFamily: MONO_FONT, fontSize: "0.6875rem", color: colors.grey[500] }}>
                      {members.length} member{members.length !== 1 ? "s" : ""}
                    </Typography>
                    {canManage && (
                      <Box>
                        {role.syncStatus === "failed" && (
                          <Tooltip title="Retry Keycloak sync">
                            <IconButton size="small" onClick={() => handleSync(role.name)}>
                              <SyncIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!role.isDefault && role.inKeycloak && (
                          <Tooltip title="Set as default fallback role">
                            <IconButton size="small" onClick={() => handleSetDefault(role.name)}>
                              <StarBorderIcon sx={{ fontSize: 15, color: "#d97706" }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!role.isSystem && !role.isDefault && (
                          <Tooltip title="Delete role">
                            <IconButton size="small" onClick={() => setDeleteTarget(role)}>
                              <DeleteOutlineIcon sx={{ fontSize: 15, color: "#dc2626" }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {role.isDefault && (
                          <Tooltip title="The default role can't be deleted. Set another role as default first.">
                            <span>
                              <IconButton size="small" disabled>
                                <DeleteOutlineIcon sx={{ fontSize: 15, color: colors.grey[600] }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Body */}
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography sx={{ fontSize: "0.75rem", color: colors.grey[400], mb: 1, lineHeight: 1.5 }}>
                    {role.description || "No description."}
                  </Typography>
                  {allowed.length > 0 && (
                    <>
                      <Typography sx={{ fontFamily: MONO_FONT, fontSize: "0.5625rem", color: colors.grey[500],
                                        letterSpacing: "0.06em", textTransform: "uppercase", mt: 1, mb: 0.3 }}>
                        Permissions
                      </Typography>
                      {allowed.map((p) => <PermItem key={p} label={p} />)}
                    </>
                  )}
                  {denied.length > 0 && (
                    <>
                      <Typography sx={{ fontFamily: MONO_FONT, fontSize: "0.5625rem", color: colors.grey[500],
                                        letterSpacing: "0.06em", textTransform: "uppercase", mt: 1, mb: 0.3 }}>
                        Denied
                      </Typography>
                      {denied.map((p) => <PermItem key={p} label={p} denied />)}
                    </>
                  )}
                  {permsEntries.length === 0 && (
                    <Typography sx={{ fontSize: "0.6875rem", color: colors.grey[500] }}>
                      No explicit permissions set.
                    </Typography>
                  )}
                </Box>

                {/* Footer */}
                <Box sx={{ px: 2, py: 1.25, borderTop: `1px solid ${theme.palette.divider}`,
                           display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <AvatarStack members={members} />
                  <Typography sx={{ fontSize: "0.625rem", color: colors.grey[500] }}>
                    {role.inKeycloak ? "In Keycloak" : "Catalog only"}
                  </Typography>
                </Box>
              </Box>
            );
          })}
          {roles.length === 0 && (
            <Typography sx={{ fontSize: "0.75rem", color: colors.grey[500] }}>
              No roles yet. Create one to get started.
            </Typography>
          )}
        </Box>
      )}

      {/* Create Role dialog */}
      <Dialog open={dialogOpen} onClose={() => !busy && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 600 }}>Create role</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.75rem", color: colors.grey[500], mb: 2 }}>
            The role is saved to the catalog and created in this business’s Keycloak realm. If
            Keycloak is unreachable, the role is kept and you can retry the sync.
          </Typography>
          <TextField
            label="Role name (slug)" fullWidth size="small" margin="dense"
            placeholder="data_scientist"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            helperText="Lowercase letters, digits, underscore. Becomes the Keycloak realm role."
          />
          <TextField
            label="Display name" fullWidth size="small" margin="dense"
            value={form.displayName}
            onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
          />
          <TextField
            label="Description" fullWidth size="small" margin="dense" multiline minRows={2}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
          <Typography sx={{ fontFamily: MONO_FONT, fontSize: "0.625rem", color: colors.grey[500],
                            letterSpacing: "0.06em", textTransform: "uppercase", mt: 2, mb: 0.5 }}>
            Permissions
          </Typography>
          {PERMISSION_OPTIONS.map(([key, label]) => (
            <FormControlLabel
              key={key}
              control={<Checkbox size="small" checked={!!form.permissions[key]} onChange={() => togglePerm(key)} />}
              label={<Typography sx={{ fontSize: "0.75rem" }}>{label} <Box component="span" sx={{ fontFamily: MONO_FONT, color: colors.grey[500] }}>({key})</Box></Typography>}
              sx={{ display: "flex", m: 0 }}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={busy}>
            {busy ? "Creating…" : "Create role"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Role confirmation — previews who gets moved to the default role */}
      <DeleteRoleModal
        open={Boolean(deleteTarget)}
        role={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </Box>
  );
};

export default RolesTab;
